import { memo, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { isNil } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import Store
import { useAppDispatch, useAppSelector } from 'store';
import { selectBaseQueryConfigurations } from 'store/slices/queryConfigurations';
// Import UI components
import { Tabs, Tab, BusyLoader } from '@geneui/components';
// Import Components
import CompositeReportInfoTab from './CompositeReportInfoTab';
// Import Services
import { ReportHttpService } from 'services/http';
import { getQueryConfigurationsList } from 'services/segment';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';

const CompositeReportInfoTabs = ({ compositeConfiguration, segmentFiltersData, setSegmentFiltersData }) => {
    const { t } = useTranslation();
    const { doGetRequest, doPostRequest } = useRequest();
    const dispatch = useAppDispatch();

    const queryConfigurations = useAppSelector(selectBaseQueryConfigurations);

    const [isLoading, setIsLoading] = useState(false);
    const [childReports, setChildReports] = useState<AnyObject[]>();
    const { getReportById, getReportsByReport } = useCancelableRequests({
        getReportById: ReportHttpService.getReportById,
        getReportsByReport: ReportHttpService.getReportsByReport,
    });
    // Not an HTTP request object (its request returns void), so it stays out of useCancelableRequests
    const { getQueryConfigurationsListRequest } = useMemo(
        () => ({
            getQueryConfigurationsListRequest: getQueryConfigurationsList(),
        }),
        [],
    );

    const getReports = (reportIdsAndTypes) => {
        return reportIdsAndTypes.map((report) => {
            return doGetRequest(getReportById.request, {
                queryString: {
                    reportId: report.ReportId,
                    reportType: report.ReportType,
                },
            });
        });
    };

    const mapReportsInfo = (responses) => {
        // for promise all need has error but case dows not cover
        let hasError = false;
        const segmentsData = responses.map((response) => {
            // for cases when promise is null
            if (isNil(response)) return response;

            const { HasError, Data } = response;
            if (!HasError) {
                return Data;
            }
            // case when one of one promise has error
            hasError = true;
            return null;
        });
        return { Data: segmentsData, HasError: hasError };
    };

    const mapChildReports = (reportsData) => {
        return reportsData.map((report) => {
            const reportType = compositeConfiguration.Children.find(
                (child) => child.ReportId === report.ReportId,
            ).ReportType;
            return { ...report, reportType };
        });
    };

    const tabChangeHandler = (tabIndex: string | number) => {
        if (!isNil(childReports) && isNil(childReports[tabIndex]?.usedIn)) {
            doPostRequest(getReportsByReport.request, {
                requestBody: {
                    reportId: childReports[tabIndex]?.ReportId,
                    reportType: childReports[tabIndex]?.reportType,
                },
                successCallback: (Data) => {
                    const reportsWithUsedIn = [...childReports];
                    reportsWithUsedIn[tabIndex] = { ...reportsWithUsedIn[tabIndex], usedIn: Data };
                    setChildReports(reportsWithUsedIn);
                },
            });
        }

        getQueryConfigurationsListRequest.request(
            dispatch,
            queryConfigurations,
            childReports?.[tabIndex],
            {
                shouldNotDispatch: true,
                setData: setSegmentFiltersData,
            },
            t,
        );
    };

    const init = () => {
        setIsLoading(true);
        if (!isNil(compositeConfiguration)) {
            Promise.all(getReports(compositeConfiguration.Children)).then((responses) => {
                const mapReportsData = mapReportsInfo(responses);
                const data = mapChildReports(mapReportsData.Data);
                setChildReports(data);
                setIsLoading(mapReportsData.HasError);
            });
        }
    };

    useEffect(() => {
        if (!isLoading && !isNil(childReports)) {
            tabChangeHandler(0); //for first time when loading
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, [compositeConfiguration]);

    return (
        <div className="composite-report-tabs">
            <BusyLoader isBusy={isLoading || isNil(childReports)} type="spinner" spinnerSize="medium">
                {!isLoading && !isNil(childReports) && (
                    <Tabs type="box" position="top" onChange={tabChangeHandler}>
                        {childReports.map((report, index) => (
                            <Tab key={index} title={report.Name}>
                                <CompositeReportInfoTab
                                    segmentFiltersData={segmentFiltersData}
                                    key={index}
                                    report={report}
                                />
                            </Tab>
                        ))}
                    </Tabs>
                )}
            </BusyLoader>
        </div>
    );
};

CompositeReportInfoTabs.propTypes = {
    compositeConfiguration: PropTypes.object.isRequired,
    segmentFiltersData: PropTypes.objectOf(
        PropTypes.shape({
            id: PropTypes.number,
            isMain: PropTypes.bool,
            seqId: PropTypes.number,
            name: PropTypes.string,
            filters: PropTypes.objectOf(PropTypes.array),
        }),
    ),
    setSegmentFiltersData: PropTypes.func,
};

export default memo(CompositeReportInfoTabs);
